'use client';

import React, { useCallback, useRef } from 'react';

interface ActionButtonsProps {
  isAirborne: boolean;
  comboWindowActive: boolean;
  onJump: () => void;
  onSpin: () => void;
  onTrick: () => void;
  onCombo: () => void;
}

interface ActionButtonDef {
  id: string;
  label: string;
  sublabel: string;
  color: string;
  bgColor: string;
  borderColor: string;
  handler: () => void;
  size: 'large' | 'small';
}

export default function ActionButtons({
  isAirborne,
  comboWindowActive,
  onJump,
  onSpin,
  onTrick,
  onCombo,
}: ActionButtonsProps) {
  const touchStartTimes = useRef<Record<string, number>>({});

  const handleTouchStart = useCallback(
    (id: string, handler: () => void) => (e: React.TouchEvent) => {
      e.preventDefault();
      touchStartTimes.current[id] = Date.now();
      handler();
    },
    []
  );

  const groundButtons: ActionButtonDef[] = [
    {
      id: 'btn-jump',
      label: 'JUMP',
      sublabel: 'Air',
      color: '#00e5ff',
      bgColor: 'rgba(0,229,255,0.12)',
      borderColor: 'rgba(0,229,255,0.35)',
      handler: onJump,
      size: 'large',
    },
    {
      id: 'btn-spin',
      label: 'SPIN',
      sublabel: '360°',
      color: '#ffd60a',
      bgColor: 'rgba(255,214,10,0.10)',
      borderColor: 'rgba(255,214,10,0.3)',
      handler: onSpin,
      size: 'small',
    },
  ];

  const airButtons: ActionButtonDef[] = [
    {
      id: 'btn-trick',
      label: 'TRICK',
      sublabel: isAirborne ? 'Do it!' : 'Air only',
      color: '#ffd60a',
      bgColor: 'rgba(255,214,10,0.12)',
      borderColor: 'rgba(255,214,10,0.35)',
      handler: onTrick,
      size: 'large',
    },
    {
      id: 'btn-combo',
      label: comboWindowActive ? 'COMBO!' : 'COMBO',
      sublabel: comboWindowActive ? 'Chain it!' : 'After land',
      color: comboWindowActive ? '#ff4d6d' : '#ff4d6d',
      bgColor: comboWindowActive ? 'rgba(255,77,109,0.2)' : 'rgba(255,77,109,0.10)',
      borderColor: comboWindowActive ? 'rgba(255,77,109,0.6)' : 'rgba(255,77,109,0.3)',
      handler: onCombo,
      size: 'small',
    },
  ];

  const buttons = isAirborne ? airButtons : groundButtons;

  return (
    <div
      className="w-full px-3 pb-safe-bottom pb-6 pt-2"
      style={{
        background: 'linear-gradient(to top, rgba(13,13,15,0.85) 0%, transparent 100%)',
      }}
    >
      <div className="flex items-end justify-between gap-3">
        {/* Primary action — large */}
        {buttons.filter(b => b.size === 'large').map(btn => (
          <button
            key={btn.id}
            className="btn-action flex-1 flex flex-col items-center justify-center rounded-2xl py-4 transition-all duration-100"
            style={{
              background: btn.bgColor,
              border: `2px solid ${btn.borderColor}`,
              boxShadow: `0 0 16px ${btn.color}22, inset 0 1px 0 rgba(255,255,255,0.06)`,
              minHeight: '72px',
            }}
            onTouchStart={handleTouchStart(btn.id, btn.handler)}
            onTouchEnd={e => e.preventDefault()}
          >
            <span
              className="text-xl font-bold tracking-wider leading-none"
              style={{ color: btn.color, fontFamily: 'DM Sans, sans-serif', textShadow: `0 0 10px ${btn.color}` }}
            >
              {btn.label}
            </span>
            <span
              className="text-[10px] font-medium mt-1 tracking-widest uppercase"
              style={{ color: btn.color + '99', fontFamily: 'DM Sans, sans-serif' }}
            >
              {btn.sublabel}
            </span>
          </button>
        ))}

        {/* Secondary action — smaller */}
        {buttons.filter(b => b.size === 'small').map(btn => (
          <button
            key={btn.id}
            className="btn-action flex flex-col items-center justify-center rounded-2xl py-3 px-4 transition-all duration-100"
            style={{
              background: btn.bgColor,
              border: `2px solid ${btn.borderColor}`,
              boxShadow: `0 0 12px ${btn.color}1a`,
              minHeight: '64px',
              minWidth: '88px',
            }}
            onTouchStart={handleTouchStart(btn.id, btn.handler)}
            onTouchEnd={e => e.preventDefault()}
          >
            <span
              className="text-base font-bold tracking-wider leading-none"
              style={{ color: btn.color, fontFamily: 'DM Sans, sans-serif' }}
            >
              {btn.label}
            </span>
            <span
              className="text-[9px] font-medium mt-1 tracking-widest uppercase"
              style={{ color: btn.color + '88', fontFamily: 'DM Sans, sans-serif' }}
            >
              {btn.sublabel}
            </span>
          </button>
        ))}
      </div>

      {/* Button mode label */}
      <p
        className="text-center text-[10px] font-medium tracking-widest uppercase mt-2"
        style={{ color: '#334455', fontFamily: 'DM Sans, sans-serif' }}
      >
        {isAirborne ? '▲ Airborne Mode' : '◆ Ground Mode'}
      </p>
    </div>
  );
}